/**
 * Centralized embed consent module.
 *
 * Combines two consent inputs into a single resolved ConsentState that consumers
 * (analytics, marketing, etc.) can read or subscribe to:
 *
 *   1. Host CMP (Consent Management Platform) — the embedding page tells us about its visitor's consent via the
 *      `calendly.consent.update.v1` postMessage. Normalized via `consent_normalize.js`.
 *   2. Calendly's own OneTrust integration — runtime active groups from the OneTrust
 *      script (mapped from C00xx group IDs to IAB-style (Interactive Advertising Bureau) category aliases).
 *
 * Precedence (per category):
 *   - If the host has emitted a categorical decision ('granted' or 'denied'), host wins.
 *   - If the host has emitted 'unknown' for a category, fall back to OneTrust for that
 *     category.
 *   - If neither has emitted a decision for a category, apply the unknown-state policy.
 *
 * Unknown-state policy (when no signal exists for a category):
 *   - necessary    -> 'granted' (necessary cookies are always allowed; matches GDPR norms)
 *   - functional   -> 'denied'
 *   - performance  -> 'denied'
 *   - targeting    -> 'denied'
 *
 * Resolved categories never expose 'unknown' — getState() always returns a binary
 * 'granted' | 'denied' decision per category so consumers don't need to reimplement
 * the unknown policy themselves.
 *
 * Provenance (`source` on the resolved state):
 *   - 'host'     if any host signal is set
 *   - 'onetrust' if no host signal and any OneTrust signal is set
 *   - 'default'  if neither has emitted anything (state is the unknown-state policy default)
 *
 * onChange semantics: fires only on real resolved-state changes. Updates that produce a
 * structurally identical resolved state (e.g. host re-emits the same categories with a
 * different `updatedAt`, or framework alone changes) do NOT fire subscribers. This
 * matches the "fires only on real state changes" acceptance criterion.
 */

import { parseQuery } from '@calendly/http-tools'

import { CONSENT_CATEGORIES, normalizeHostConsent } from './consent_normalize'

const UNKNOWN_STATE_POLICY = Object.freeze({
  necessary: 'granted',
  functional: 'denied',
  performance: 'denied',
  targeting: 'denied',
})

// OneTrust group IDs → IAB-style category aliases. Mirrors @calendly/onetrust's
// internal mapping (functional/performance/targeting) and adds the standard
// necessary group (C0001), which OneTrust treats as always-on but does emit when
// the visitor has accepted.
const ONETRUST_GROUP_TO_CATEGORY = Object.freeze({
  C0001: 'necessary',
  C0002: 'performance',
  C0003: 'functional',
  C0004: 'targeting',
})

/**
 * Normalize the OneTrust input passed to `updateFromOneTrust`. Accepts:
 *   - `null` to clear the OneTrust signal entirely.
 *   - `{ activeGroups: string[] }` (the shape produced by `@calendly/onetrust`'s
 *     redux slice as well as the `Onetrust` component's `setActiveGroups` callback).
 *   - `string[]` directly, for callers that have just the active-groups list.
 *
 * Categories not represented by an active group are emitted as 'denied'. Necessary
 * is special-cased to 'granted' if it's missing (OneTrust often omits it from the
 * activeGroups array because it's always-on; treating absence as denied would be
 * incorrect).
 */
const normalizeOneTrustInput = (input) => {
  if (input == null) return null

  let activeGroups
  if (Array.isArray(input)) {
    activeGroups = input
  } else if (input && typeof input === 'object' && Array.isArray(input.activeGroups)) {
    activeGroups = input.activeGroups
  } else {
    return null
  }

  const groupSet = new Set(activeGroups.filter((g) => typeof g === 'string'))
  const categories = {}
  CONSENT_CATEGORIES.forEach((category) => {
    const groupId = Object.keys(ONETRUST_GROUP_TO_CATEGORY)
      .find((id) => ONETRUST_GROUP_TO_CATEGORY[id] === category)
    if (groupId && groupSet.has(groupId)) {
      categories[category] = 'granted'
    } else if (category === 'necessary') {
      categories[category] = 'granted'
    } else {
      categories[category] = 'denied'
    }
  })

  return { categories }
}

const resolveCategory = (category, host, onetrust) => {
  const hostValue = host?.categories?.[category]
  if (hostValue === 'granted' || hostValue === 'denied') return hostValue

  // hostValue is 'unknown' OR host has no opinion for this category → fall back to OneTrust.
  const onetrustValue = onetrust?.categories?.[category]
  if (onetrustValue === 'granted' || onetrustValue === 'denied') return onetrustValue

  return UNKNOWN_STATE_POLICY[category]
}

const resolveSource = (host, onetrust) => {
  if (host) return 'host'
  if (onetrust) return 'onetrust'
  return 'default'
}

const buildResolvedState = (host, onetrust) => {
  const categories = {}
  CONSENT_CATEGORIES.forEach((category) => {
    categories[category] = resolveCategory(category, host, onetrust)
  })
  return {
    categories,
    source: resolveSource(host, onetrust),
    host,
    onetrust,
  }
}

// onChange-equality: compare resolved categories + source. We deliberately ignore
// `host.updatedAt`, `host.framework`, and the raw input shapes so subscribers don't
// fire when only metadata changed. If a consumer later needs metadata-level change
// detection, expose a separate subscribe API for it.
const resolvedStatesEqual = (a, b) => {
  if (a.source !== b.source) return false
  for (let i = 0; i < CONSENT_CATEGORIES.length; i += 1) {
    const k = CONSENT_CATEGORIES[i]
    if (a.categories[k] !== b.categories[k]) return false
  }
  return true
}

export const createConsentModule = () => {
  let host = null
  let onetrust = null
  let state = buildResolvedState(host, onetrust)
  const subscribers = new Set()

  const notify = () => {
    subscribers.forEach((cb) => {
      try {
        cb(state)
      } catch {
        /* noop — one bad subscriber must not break the rest */
      }
    })
  }

  const recompute = () => {
    const next = buildResolvedState(host, onetrust)
    if (resolvedStatesEqual(state, next)) {
      // Still update the stored snapshot so callers reading getState() see the latest
      // raw inputs (host.updatedAt, framework, etc.) even when resolved state is unchanged.
      state = next
      return
    }
    state = next
    notify()
  }

  return {
    getState: () => state,

    /**
     * Apply a host CMP signal. Pass the raw v1 `consent` payload (i.e. `data.consent`
     * from a `calendly.consent.update.v1` postMessage). Pass `null` to clear.
     * Invalid payloads (missing source, no recognized categories) are dropped silently.
     */
    updateFromHost: (raw) => {
      const next = raw == null ? null : normalizeHostConsent(raw)
      if (raw != null && next == null) return
      host = next
      recompute()
    },

    /**
     * Apply a OneTrust signal. Accepts `{ activeGroups: string[] }`, a bare string[],
     * or `null` to clear.
     */
    updateFromOneTrust: (input) => {
      const next = normalizeOneTrustInput(input)
      onetrust = next
      recompute()
    },

    onChange: (callback) => {
      subscribers.add(callback)
      return () => subscribers.delete(callback)
    },
  }
}

/**
 * Default singleton consent module. Use this for app-wide reads/writes; use
 * `createConsentModule()` directly for tests or scoped instances.
 */
export const consentModule = createConsentModule()

// Backward-compat: synchronously seed the singleton when the iframe was loaded with
// `hide_gdpr_banner=true` AND a real embed context (`embed_domain` + `embed_type`),
// and the host has not yet sent a `calendly.consent.update.v1` message. Runs at
// module evaluation so `getState()` returns the seeded state immediately — matching
// the synchronous bypass the legacy `shouldHideOnetrustSelector` provided.
//
// The legacy selector required `shouldHide && isEmbedded` (see `selectors/gdpr.js`
// pre-refactor), where `isEmbedded` was derived from `embed_domain && embed_type`
// in `reducers/gdpr.js`. We preserve that two-factor gate so a non-embedded page
// that carries `?hide_gdpr_banner=true` does not silently grant every category —
// that would be a privacy regression vs. the old selector. The matching gate in
// `reducers/consent.js` keeps Redux in sync from the very first render; this keeps
// anything that reads `consentModule` directly (e.g. `ConsentStatePublisher`) on
// the same page. Performed in try/catch so a malformed URL can never break module
// evaluation.
try {
  if (typeof window !== 'undefined') {
    const {
      hide_gdpr_banner: hideGdprBanner,
      embed_domain: embedDomain,
      embed_type: embedType,
    } = parseQuery(window.location.search)
    if (hideGdprBanner && embedDomain && embedType) {
      consentModule.updateFromHost({
        source: 'hide_gdpr_banner',
        categories: Object.fromEntries(CONSENT_CATEGORIES.map((c) => [c, 'granted'])),
      })
    }
  }
} catch {
  /* noop — module evaluation must never throw */
}
